import { useMemo } from 'react';
import type { PassState } from '../../../../store/redux/slices/pass.slice';
import type { PassTemplateMeta } from '../../../../store/types/pass';
import { buildPassPreviewData } from '../utils/buildPassPreviewData';
import {
  buildFieldOverrides,
  buildLocalisedLabelOverrides,
} from '../utils/passEditState';
import { buildTemplateMeta } from '../utils/buildTemplateMeta';
import { getStoredFeaturedActions } from '../utils/buildPassEnhancedPreview';
import { usePassEnhancedPreview } from './usePassEnhancedPreview';

interface UsePassPreviewDataParams {
  pass: any;
  passState: PassState;
  passTemplate: PassTemplateMeta | undefined;
  templateDetail: any;
  isEdit?: boolean;
  isCreate?: boolean;
  selectedPassLanguage: string | null;
  uuid?: string;
}

export const usePassPreviewData = ({
  pass,
  passState,
  passTemplate,
  templateDetail,
  isEdit,
  isCreate,
  selectedPassLanguage,
  uuid,
}: UsePassPreviewDataParams) => {
  const template =
    passTemplate ||
    (isCreate && templateDetail?.data?.id
      ? buildTemplateMeta(
          templateDetail.data as {
            id: string;
            attributes: Record<string, unknown>;
          }
        )
      : undefined);

  const templateImages = (
    templateDetail?.data?.attributes as Record<string, unknown>
  )?.images as
    | {
        logo?: string;
        icon?: string;
        banner?: string;
        apple?: {
          logo?: string;
          icon?: string;
          banner?: string;
          thumbnail?: string;
        };
        google?: { logo?: string; banner?: string; wideLogo?: string };
      }
    | undefined;

  const templateAttrsRaw = templateDetail?.data?.attributes as
    | Record<string, unknown>
    | undefined;

  const basePreviewData = useMemo(() => {
    if (pass && template) {
      return buildPassPreviewData(pass, template, templateImages);
    }
    if (isCreate && template) {
      const stubPass = {
        id: '',
        attributes: {
          extId: '',
          passType: template.template_type,
          status: null,
          deviceType: 'iphone' as const,
          createdAt: '',
          addedAt: '',
          downloadUrl: '',
          deactivatedAt: null,
          passContent: {},
          passDesign: null,
          barcode: passState.barcode,
          nfc: null,
          groupTag: null,
          groupTags: [],
          expiresAt: null,
        },
      };
      return buildPassPreviewData(stubPass, template, templateImages);
    }
    return undefined;
  }, [pass, template, templateImages, isCreate, passState.barcode]);

  const focusedField = passState.focusedField;

  const resolvedPreviewData = useMemo(() => {
    if (!basePreviewData) return undefined;
    if (!isEdit && !isCreate) return basePreviewData;

    const result = { ...basePreviewData };

    if (template) {
      Object.assign(
        result,
        buildFieldOverrides(template, passState.fieldValues)
      );
    }

    const resolvedBarcodeValue = (() => {
      if (!passState.barcode.enabled) return '';
      if (passState.barcode.source === 'uuid') return uuid || pass?.id || '';
      if (passState.barcode.source === 'extId')
        return passState.extId || pass?.attributes?.extId || '';
      return passState.barcode.value || '';
    })();

    result.barcode = {
      enabled: passState.barcode.enabled,
      type: passState.barcode.type || 'qr',
      source: passState.barcode.source || 'uuid',
      value: resolvedBarcodeValue,
      displayText: passState.barcode.displayText ?? false,
    };

    result.nfc = {
      enabled: passState.nfcEnabled,
      appleNfc: passState.nfcEnabled,
      googleNfc: passState.nfcEnabled,
      source: passState.nfcSource as 'uuid' | 'extId' | 'custom',
      customValue:
        passState.nfcSource === 'custom' ? passState.nfcCustomValue : '',
    };
    result.googleNfcEnabled = passState.nfcEnabled;

    // Apply translation overrides when a language is selected
    if (selectedPassLanguage && template) {
      const templateFieldsRaw = templateAttrsRaw?.fields as
        | Record<
            string,
            Record<
              string,
              {
                localisation?: Record<
                  string,
                  { label?: string; defaultValue?: string }
                >;
              } | null
            >
          >
        | undefined;
      Object.assign(
        result,
        buildLocalisedLabelOverrides(templateFieldsRaw, selectedPassLanguage)
      );

      const translatedValues: Record<string, string> = {};
      Object.entries(passState.fieldLocalisations).forEach(
        ([fieldId, locales]) => {
          if (locales[selectedPassLanguage]) {
            translatedValues[fieldId] = locales[selectedPassLanguage];
          }
        }
      );
      if (Object.keys(translatedValues).length > 0) {
        Object.assign(result, buildFieldOverrides(template, translatedValues));
      }
    }

    if (isEdit || isCreate) {
      result.focusedField = focusedField;
      result.isFieldHighlighted = (fieldIdentifier: string) => {
        if (!focusedField) return false;
        // Only highlight the Google NFC badge for non-loyalty/stamp card types — loyalty and stamp card
        // templates have a separate "Use contactless mode on Google Wallet" toggle on the template
        if (
          fieldIdentifier === 'nfc.googleNfc' &&
          (template?.template_type === 'loyalty' ||
            template?.template_type === 'storeCard' ||
            template?.template_type === 'stampCard')
        )
          return false;
        if (focusedField === fieldIdentifier) return true;
        const focusedDotIndex = focusedField.lastIndexOf('.');
        const identifierDotIndex = fieldIdentifier.lastIndexOf('.');
        if (focusedDotIndex === -1 || identifierDotIndex === -1) return false;
        const focusedPrefix = focusedField.substring(0, focusedDotIndex);
        const identifierPrefix = fieldIdentifier.substring(
          0,
          identifierDotIndex
        );
        return focusedPrefix === identifierPrefix;
      };
    }

    return result;
  }, [
    basePreviewData,
    passState,
    template,
    isEdit,
    isCreate,
    selectedPassLanguage,
    focusedField,
  ]);

  const usesLiveFeaturedActions =
    isCreate || (isEdit && passState.isInitialised);

  const previewData = usePassEnhancedPreview(
    templateAttrsRaw,
    resolvedPreviewData,
    usesLiveFeaturedActions
      ? passState.featuredActions
      : getStoredFeaturedActions(pass)
  );

  return {
    template,
    templateAttrsRaw,
    previewData,
  };
};
